const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useEffect, useState } from 'react';
import { Award, Gem, Heart, History, Users, ShieldCheck, Map, Truck } from 'lucide-react';

const About = ({ onNavigate, settings }) => {
  const [scrollY, setScrollY] = useState(0);

  const address =
    _optionalChain([settings, 'optionalAccess', (_) => _.address]) || 'Luxury Lane, Makati';
  const phone = _optionalChain([settings, 'optionalAccess', (_2) => _2.phone]) || '0945 392 8946';
  const hours =
    _optionalChain([settings, 'optionalAccess', (_3) => _3.hours]) || 'Mon - Sat: 10am - 7pm';

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return React.createElement(
    'div',
    {
      className: 'bg-stone-50 min-h-screen text-stone-900 overflow-hidden',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 24 },
    },

    /* 1. CINEMATIC HERO */
    React.createElement(
      'section',
      {
        className: 'relative h-[80vh] w-full overflow-hidden flex items-center justify-center',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 27 },
      },
      React.createElement(
        'div',
        {
          className: 'absolute inset-0',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 28 },
        },
        /* Updated Hero Image: Luxury Jewelry Store Interior/Display */
        React.createElement('img', {
          src: 'https://images.unsplash.com/photo-1522312346375-d1a52e2b99b3?auto=format&fit=crop&w=2070&q=80',
          alt: 'Jewelry Workshop',
          className: 'w-full h-full object-cover animate-[zoomSlow_25s_infinite_alternate]',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 30 },
        }),

        /* Enhanced Overlay for Readability */
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/50 mix-blend-multiply',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 37 },
        }),
        React.createElement('div', {
          className: 'absolute inset-0 bg-black/40',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 38 },
        }),
        React.createElement('div', {
          className:
            'absolute inset-0 bg-gradient-to-t from-stone-900/90 via-transparent to-stone-900/40',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 39 },
        })
      ),

      React.createElement(
        'div',
        {
          className: 'relative z-10 text-center px-6 max-w-4xl mx-auto',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 42 },
        },
        React.createElement(
          'span',
          {
            className:
              'inline-block text-gold-300 text-xs tracking-[0.4em] uppercase mb-6 animate-fade-in font-bold drop-shadow-md',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 43 },
          },
          'The Maison'
        ),
        React.createElement(
          'h1',
          {
            className:
              'font-serif text-5xl md:text-7xl text-white mb-8 leading-tight drop-shadow-2xl animate-fade-in delay-100',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 46 },
          },
          'A Legacy of ',
          React.createElement('br', {
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 47 },
          }),
          React.createElement(
            'span',
            {
              className: 'italic text-gold-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 48 },
            },
            'Perfection'
          )
        ),
        React.createElement(
          'p',
          {
            className:
              'text-white/90 text-lg font-light max-w-xl mx-auto leading-relaxed animate-fade-in delay-200 backdrop-blur-md bg-white/10 p-6 border border-white/20 rounded-sm shadow-xl',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 50 },
          },
          'For over four decades, Sparkling Gems has defined the standard of luxury in the Philippines, curating treasures that transcend time.'
        )
      )
    ),

    /* 2. BRAND TICKER */
    React.createElement(
      'div',
      {
        className:
          'bg-stone-900 py-6 border-y border-gold-600/30 overflow-hidden whitespace-nowrap relative z-20',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 57 },
      },
      React.createElement(
        'div',
        {
          className:
            'inline-block animate-[scroll_30s_linear_infinite] text-gold-400/80 text-xs tracking-[0.4em] uppercase font-medium',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 58 },
        },
        React.createElement(
          'span',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 59 } },
          'Established 1985 • Manila • '
        ),
        React.createElement(
          'span',
          {
            className: 'mx-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 60 },
          },
          'GIA Alumni Members • '
        ),
        React.createElement(
          'span',
          {
            className: 'mx-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 61 },
          },
          'Conflict-Free Diamonds • '
        ),
        React.createElement(
          'span',
          {
            className: 'mx-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 62 },
          },
          'Bespoke Artisanship • '
        ),
        React.createElement(
          'span',
          {
            className: 'mx-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 63 },
          },
          'Heritage Restoration • '
        ),
        React.createElement(
          'span',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 64 } },
          'Established 1985 • Manila • '
        ),
        React.createElement(
          'span',
          {
            className: 'mx-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 65 },
          },
          'GIA Alumni Members • '
        ),
        React.createElement(
          'span',
          {
            className: 'mx-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 66 },
          },
          'Conflict-Free Diamonds • '
        ),
        React.createElement(
          'span',
          {
            className: 'mx-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 67 },
          },
          'Bespoke Artisanship • '
        ),
        React.createElement(
          'span',
          {
            className: 'mx-12',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 68 },
          },
          'Heritage Restoration • '
        )
      )
    ),

    /* 3. OUR STORY - ALTERNATING LAYOUT */
    React.createElement(
      'section',
      {
        className: 'py-32 px-6 max-w-7xl mx-auto',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 73 },
      },
      /* Section 1 */
      React.createElement(
        'div',
        {
          className: 'flex flex-col md:flex-row items-center gap-16 mb-32 group',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 75 },
        },
        React.createElement(
          'div',
          {
            className: 'w-full md:w-1/2 relative',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 76 },
          },
          React.createElement('div', {
            className:
              'absolute top-4 -left-4 w-full h-full border border-gold-200 transition-transform duration-700 group-hover:translate-x-2 group-hover:translate-y-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 77 },
          }),
          React.createElement(
            'div',
            {
              className: 'relative overflow-hidden aspect-[4/5] bg-stone-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 78 },
            },
            /* Updated Image: Jeweler working / Hands */
            React.createElement('img', {
              src: 'https://images.unsplash.com/photo-1626784215021-2e39ccf971cd?q=80&w=2070&auto=format&fit=crop',
              alt: 'The Founder',
              className:
                'w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 filter sepia-[0.2]',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 80 },
            })
          )
        ),
        React.createElement(
          'div',
          {
            className: 'w-full md:w-1/2 text-center md:text-left',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 87 },
          },
          React.createElement(
            'div',
            {
              className:
                'flex items-center gap-4 justify-center md:justify-start mb-6 text-gold-600',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 88 },
            },
            React.createElement(History, {
              size: 24,
              strokeWidth: 1,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 89 },
            }),
            React.createElement(
              'span',
              {
                className: 'text-xs tracking-[0.3em] uppercase font-bold',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 90 },
              },
              'The Beginning'
            )
          ),
          React.createElement(
            'h2',
            {
              className: 'font-serif text-4xl text-stone-900 mb-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 92 },
            },
            '1985: A Humble Start'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-600 font-light leading-relaxed mb-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 93 },
            },
            'It began with a single magnifying loupe and a passion for brilliance. Our founder, traveling through the gem markets of Southeast Asia, envisioned a maison in Manila that would rival the great jewelers of Europe.'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-600 font-light leading-relaxed',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 96 },
            },
            'What started as a small workshop has grown into a sanctuary for collectors, yet the core principle remains: every stone must tell a story.'
          ),
          React.createElement('div', {
            className: 'mt-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 99 },
          })
        )
      ),

      /* Section 2 */
      React.createElement(
        'div',
        {
          className: 'flex flex-col md:flex-row-reverse items-center gap-16 group',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 105 },
        },
        React.createElement(
          'div',
          {
            className: 'w-full md:w-1/2 relative',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 106 },
          },
          React.createElement('div', {
            className:
              'absolute bottom-4 -right-4 w-full h-full border border-stone-300 transition-transform duration-700 group-hover:-translate-x-2 group-hover:-translate-y-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 107 },
          }),
          React.createElement(
            'div',
            {
              className: 'relative overflow-hidden aspect-[4/3] bg-stone-200',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 108 },
            },
            React.createElement('img', {
              src: 'https://images.unsplash.com/photo-1522312346375-d1a52e2b99b3?auto=format&fit=crop&w=2148&q=80',
              alt: 'Modern Showroom',
              className:
                'w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 109 },
            })
          )
        ),
        React.createElement(
          'div',
          {
            className: 'w-full md:w-1/2 text-center md:text-left',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 116 },
          },
          React.createElement(
            'div',
            {
              className:
                'flex items-center gap-4 justify-center md:justify-start mb-6 text-gold-600',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 117 },
            },
            React.createElement(Gem, {
              size: 24,
              strokeWidth: 1,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 118 },
            }),
            React.createElement(
              'span',
              {
                className: 'text-xs tracking-[0.3em] uppercase font-bold',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 119 },
              },
              'The Evolution'
            )
          ),
          React.createElement(
            'h2',
            {
              className: 'font-serif text-4xl text-stone-900 mb-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 121 },
            },
            'Curating the Extraordinary'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-600 font-light leading-relaxed mb-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 122 },
            },
            'Today, Sparkling Gems is not just a retailer but a curator of investment-grade assets. We blend traditional craftsmanship with modern technology, using 3D modeling for bespoke commissions while maintaining hand-forged techniques for assembly.'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-600 font-light leading-relaxed',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 125 },
            },
            'Our curated collection now includes rare vintage timepieces and designer handbags, authenticated by our in-house experts.'
          )
        )
      )
    ),

    /* 4. PARALLAX ATELIER SECTION */
    /* Changed background image to be a distinct photo of jewelry tools/craftsmanship to emphasize 'Authenticity' */
    React.createElement(
      'section',
      {
        className: 'relative py-40 bg-fixed bg-center bg-cover',
        style: {
          backgroundImage:
            "url('https://images.unsplash.com/photo-1600180758895-7d7f6d7b4f5c?auto=format&fit=crop&w=2070&q=80')",
        },
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 134 },
      },

      React.createElement('div', {
        className: 'absolute inset-0 bg-stone-900/75',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 141 },
      }),
      React.createElement('div', {
        className:
          "absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10",
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 142 },
      }),

      React.createElement(
        'div',
        {
          className: 'relative z-10 max-w-5xl mx-auto px-6 text-center text-white',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 144 },
        },
        React.createElement(ShieldCheck, {
          size: 48,
          className: 'mx-auto text-gold-400 mb-8 animate-pulse',
          strokeWidth: 1,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 145 },
        }),
        React.createElement(
          'h2',
          {
            className: 'font-serif text-5xl md:text-6xl mb-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 150 },
          },
          'The Pledge of Authenticity'
        ),
        React.createElement(
          'p',
          {
            className: 'text-xl font-light text-stone-200 leading-relaxed max-w-3xl mx-auto',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 153 },
          },
          '"In an age of mass production, we promise the rare. Every diamond is GIA certified. Every vintage piece is rigorously vetted. We sell nothing we would not be proud to own ourselves."'
        ),
        React.createElement(
          'div',
          {
            className:
              'mt-12 flex justify-center gap-8 text-xs uppercase tracking-widest text-gold-400',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 158 },
          },
          React.createElement(
            'div',
            {
              className: 'flex flex-col items-center gap-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 159 },
            },
            React.createElement(
              'div',
              {
                className:
                  'w-16 h-16 rounded-full border border-gold-500/50 flex items-center justify-center bg-white/5 backdrop-blur',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 160 },
              },
              '100%'
            ),
            React.createElement(
              'span',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 161 } },
              'Certified'
            )
          ),
          React.createElement(
            'div',
            {
              className: 'flex flex-col items-center gap-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 163 },
            },
            React.createElement(
              'div',
              {
                className:
                  'w-16 h-16 rounded-full border border-gold-500/50 flex items-center justify-center bg-white/5 backdrop-blur',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 164 },
              },
              'Lifetime'
            ),
            React.createElement(
              'span',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 165 } },
              'Warranty'
            )
          ),
          React.createElement(
            'div',
            {
              className: 'flex flex-col items-center gap-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 167 },
            },
            React.createElement(
              'div',
              {
                className:
                  'w-16 h-16 rounded-full border border-gold-500/50 flex items-center justify-center bg-white/5 backdrop-blur',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 168 },
              },
              'Global'
            ),
            React.createElement(
              'span',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 169 } },
              'Shipping'
            )
          )
        )
      )
    ),

    /* 5. INTERACTIVE VALUES GRID */
    React.createElement(
      'section',
      {
        className: 'py-32 bg-white relative',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 176 },
      },
      React.createElement('div', {
        className: 'absolute top-0 left-0 w-full h-full bg-noise opacity-30 pointer-events-none',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 177 },
      }),
      React.createElement(
        'div',
        {
          className: 'max-w-7xl mx-auto px-6 relative z-10',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 178 },
        },
        React.createElement(
          'div',
          {
            className: 'text-center mb-20',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 179 },
          },
          React.createElement(
            'span',
            {
              className: 'text-gold-600 text-xs tracking-[0.3em] uppercase font-bold',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 180 },
            },
            'Our Philosophy'
          ),
          React.createElement(
            'h2',
            {
              className: 'font-serif text-4xl mt-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 181 },
            },
            'Why We Exist'
          )
        ),

        React.createElement(
          'div',
          {
            className: 'grid grid-cols-1 md:grid-cols-3 gap-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 184 },
          },
          [
            {
              icon: React.createElement(Award, {
                size: 32,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 187 },
              }),
              title: 'Excellence',
              desc: "We do not compromise. From the cut of the gem to the polish of the metal, 'good enough' is never enough.",
            },
            {
              icon: React.createElement(Users, {
                size: 32,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 192 },
              }),
              title: 'Relationship',
              desc: 'We are not just selling objects; we are marking milestones. We view every client as a lifelong partner.',
            },
            {
              icon: React.createElement(Heart, {
                size: 32,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 197 },
              }),
              title: 'Passion',
              desc: 'Jewelry is emotional. We pour our heart into sourcing pieces that evoke wonder and delight.',
            },
          ].map((item, idx) =>
            React.createElement(
              'div',
              {
                key: idx,
                className:
                  'group p-10 border border-stone-100 bg-stone-50 hover:bg-stone-900 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl text-center md:text-left',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 202 },
              },
              React.createElement(
                'div',
                {
                  className:
                    'w-16 h-16 bg-white border border-stone-200 flex items-center justify-center text-stone-900 mb-8 group-hover:bg-gold-600 group-hover:text-white group-hover:border-gold-600 transition-colors duration-300 shadow-sm mx-auto md:mx-0',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 203 },
                },
                item.icon
              ),
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-2xl mb-4 group-hover:text-white transition-colors',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 206 },
                },
                item.title
              ),
              React.createElement(
                'p',
                {
                  className:
                    'text-stone-500 font-light leading-relaxed group-hover:text-stone-300 transition-colors',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 207 },
                },
                item.desc
              )
            )
          )
        )
      )
    ),

    /* 6. SHIPPING INFORMATION SECTION - ADDED HERE */
    React.createElement(
      'section',
      {
        className: 'py-16 bg-stone-50 border-y border-stone-200',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 217 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-5xl mx-auto px-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 218 },
        },
        React.createElement(
          'div',
          {
            className:
              'flex flex-col md:flex-row items-center gap-8 bg-white p-8 rounded-sm shadow-sm border border-stone-100',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 219 },
          },
          React.createElement(
            'div',
            {
              className:
                'w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center text-gold-600 flex-shrink-0',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 220 },
            },
            React.createElement(Truck, {
              size: 32,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 221 },
            })
          ),
          React.createElement(
            'div',
            {
              className: 'flex-1 text-center md:text-left',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 223 },
            },
            React.createElement(
              'h3',
              {
                className: 'font-serif text-2xl text-stone-900 mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 224 },
              },
              'Shipping Information'
            ),
            React.createElement(
              'p',
              {
                className: 'text-stone-600 mb-3',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 225 },
              },
              'We offer shipping ',
              React.createElement(
                'span',
                {
                  className: 'font-bold text-gold-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 226 },
                },
                'nationwide and worldwide'
              ),
              '. All orders are carefully packaged, insured, and tracked for your peace of mind.'
            ),
            React.createElement(
              'div',
              {
                className: 'flex flex-wrap justify-center md:justify-start gap-6 text-sm mt-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 229 },
              },
              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 230 },
                },
                React.createElement('div', {
                  className: 'w-2 h-2 bg-gold-600 rounded-full',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 231 },
                }),
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-700',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 232 },
                  },
                  React.createElement(
                    'span',
                    {
                      className: 'font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 232 },
                    },
                    'Nationwide:'
                  ),
                  ' 3-5 business days'
                )
              ),
              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 234 },
                },
                React.createElement('div', {
                  className: 'w-2 h-2 bg-gold-600 rounded-full',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 235 },
                }),
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-700',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 236 },
                  },
                  React.createElement(
                    'span',
                    {
                      className: 'font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 236 },
                    },
                    'Worldwide:'
                  ),
                  ' 7-14 business days'
                )
              ),
              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 238 },
                },
                React.createElement('div', {
                  className: 'w-2 h-2 bg-gold-600 rounded-full',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 239 },
                }),
                React.createElement(
                  'span',
                  {
                    className: 'text-stone-700',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 240 },
                  },
                  React.createElement(
                    'span',
                    {
                      className: 'font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 240 },
                    },
                    'Insurance:'
                  ),
                  ' Included on all orders'
                )
              )
            )
          )
        )
      )
    ),

    /* 7. LOCATION / VISIT */
    React.createElement(
      'section',
      {
        className: 'py-24 px-6 bg-white text-center',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 249 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-3xl mx-auto border border-gold-200 p-12 relative',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 250 },
        },
        React.createElement(
          'div',
          {
            className: 'absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 251 },
          },
          React.createElement(Map, {
            size: 32,
            className: 'text-gold-500',
            strokeWidth: 1,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 252 },
          })
        ),
        React.createElement(
          'h2',
          {
            className: 'font-serif text-4xl mb-6',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 254 },
          },
          'Visit Our Atelier'
        ),
        React.createElement(
          'p',
          {
            className: 'text-stone-500 mb-8 font-light',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 255 },
          },
          'Experience our collection in person. Private viewings are available by appointment to ensure you receive our undivided attention.'
        ),
        React.createElement(
          'div',
          {
            className: 'grid grid-cols-1 md:grid-cols-2 gap-8 text-sm text-stone-800 mb-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 258 },
          },
          React.createElement(
            'div',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 259 } },
            React.createElement(
              'h4',
              {
                className: 'font-bold uppercase tracking-widest mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 260 },
              },
              'Manila'
            ),
            React.createElement(
              'p',
              {
                className: 'font-light',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 261 },
              },
              address,
              React.createElement('br', {
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 261 },
              }),
              phone
            )
          ),
          React.createElement(
            'div',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 263 } },
            React.createElement(
              'h4',
              {
                className: 'font-bold uppercase tracking-widest mb-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 264 },
              },
              'Hours'
            ),
            React.createElement(
              'p',
              {
                className: 'font-light',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 265 },
              },
              hours
            )
          )
        ),
        React.createElement(
          'button',
          {
            onClick: () => onNavigate('contact'),
            className:
              'bg-stone-900 text-white px-8 py-3 uppercase text-xs tracking-[0.2em] hover:bg-gold-600 transition-colors',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 268 },
          },
          'Book Appointment'
        )
      )
    ),

    /* Footer note */
    React.createElement(
      'div',
      {
        className: 'bg-stone-900 text-stone-400 text-center py-6 text-xs tracking-widest',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 278 },
      },
      React.createElement(
        'p',
        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 279 } },
        '© ',
        new Date().getFullYear(),
        ' Sparkling Gems. All rights reserved. Shipping nationwide and worldwide.'
      )
    )
  );
};

export default About;
